import { LitElement, html, css, nothing } from "lit";
import { live } from "lit/directives/live.js";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import { filterIgnoredMfes } from "../snapshot-utils";
import { loadSnapshots } from "../snapshot-utils";
import { filterMfeNames, formatProductName } from "../product-utils";
import {
  buildPreviewUrl,
  addPreviewHistoryEntry,
  loadPreviewHistory,
  buildOverridesFromVersions,
  decodeDashboardPreviewParams,
  getDefaultPreviewRoute,
  type PreviewConfig,
  type PreviewHistoryEntry,
} from "../preview-utils";
import type {
  Environment,
  EnvironmentsResponse,
  MfeVersion,
  Snapshot,
  ProductsResponse,
} from "../types";
import "@bee/pattern-library";

@customElement("preview-page")
export class PreviewPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .controls {
      display: flex;
      align-items: flex-end;
      gap: var(--spacing-12, 12px);
      flex-wrap: wrap;
      margin-bottom: var(--spacing-16, 16px);
    }

    .control-group {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4, 4px);
    }

    .control-group label {
      font-size: 13px;
      font-weight: 500;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .control-group input {
      width: 300px;
      min-width: fit-content;
    }

    .control-row {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
    }

    .mfe-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: var(--spacing-16, 16px);
    }

    .mfe-table th {
      text-align: left;
      padding: var(--spacing-8, 8px) var(--spacing-12, 12px);
      font-size: 12px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      border-bottom: 2px solid var(--colors-border-border-secondary, #eaecf0);
      background: var(--colors-background-bg-secondary, #f9fafb);
    }

    .mfe-table td {
      padding: var(--spacing-8, 8px) var(--spacing-12, 12px);
      font-size: 14px;
      border-bottom: 1px solid var(--colors-border-border-secondary, #eaecf0);
      vertical-align: middle;
    }

    .mfe-table tr.overridden td {
      background: var(--colors-utility-blue-50, #eff8ff);
    }

    .mfe-name {
      font-weight: 500;
      color: var(--colors-text-brand-900, #002266);
    }

    .current-version {
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      font-family: monospace;
      font-size: 13px;
    }

    .override-cell {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
    }

    .url-bar {
      position: sticky;
      bottom: 0;
      background: var(--colors-background-bg-primary, #fff);
      border-top: 2px solid var(--colors-border-border-secondary, #eaecf0);
      padding: var(--spacing-12, 12px) 0;
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      flex-wrap: wrap;
    }

    .url-display {
      flex: 1;
      min-width: 300px;
      font-family: monospace;
      font-size: 13px;
      padding: var(--spacing-8, 8px) var(--spacing-12, 12px);
      background: var(--colors-background-bg-secondary, #f9fafb);
      border: 1px solid var(--colors-border-border-secondary, #eaecf0);
      border-radius: var(--radius-sm, 6px);
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .override-badge {
      display: inline-flex;
      align-items: center;
      padding: 2px 8px;
      border-radius: var(--radius-full, 50px);
      background: var(--colors-utility-blue-100, #d1e9ff);
      color: var(--colors-utility-blue-700, #175cd3);
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
    }

    .quick-actions {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      margin-bottom: var(--spacing-12, 12px);
    }

    .history-section {
      margin-top: var(--spacing-16, 16px);
    }

    .history-title {
      font-size: 14px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-bottom: var(--spacing-8, 8px);
    }

    .history-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4, 4px);
    }

    .history-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--spacing-8, 8px) var(--spacing-12, 12px);
      background: var(--colors-background-bg-secondary, #f9fafb);
      border: 1px solid var(--colors-border-border-secondary, #eaecf0);
      border-radius: var(--radius-sm, 6px);
      font-size: 13px;
      gap: var(--spacing-8, 8px);
    }

    .history-url {
      font-family: monospace;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      min-width: 0;
    }

    .history-meta {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      white-space: nowrap;
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
    }

    .empty {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-32, 32px);
    }

    .toast {
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: var(--colors-text-brand-900, #002266);
      color: #fff;
      padding: var(--spacing-8, 8px) var(--spacing-16, 16px);
      border-radius: var(--radius-sm, 6px);
      font-size: 13px;
      z-index: 1000;
      pointer-events: none;
    }

    select {
      padding: 6px 8px;
      border: 1px solid var(--colors-border-border-secondary, #eaecf0);
      border-radius: var(--radius-sm, 6px);
      font-size: 14px;
      font-family: Geist, sans-serif;
      background: #fff;
      color: var(--colors-text-brand-900, #002266);
    }

    input[type="text"] {
      padding: 6px 8px;
      border: 1px solid var(--colors-border-border-secondary, #eaecf0);
      border-radius: var(--radius-sm, 6px);
      font-size: 14px;
      font-family: Geist, sans-serif;
      color: var(--colors-text-brand-900, #002266);
    }
  `;

  @property({ attribute: false }) environments: EnvironmentsResponse | null = null;
  @property({ attribute: false }) activeProductFilter: string | null = null;
  @property({ attribute: false }) products: ProductsResponse | null = null;
  @property({ attribute: false }) initialEnvironment: Environment | null = null;
  @property({ attribute: false }) initialOverrides: Map<string, string> | null = null;
  @property({ attribute: false }) initialRoute: string | null = null;

  @state() private _environment: Environment = "dev";
  @state() protected _route = getDefaultPreviewRoute();
  @state() protected _overrides = new Map<string, string>();
  @state() protected _selectedSnapshotId = "";
  @state() protected _availableVersions = new Map<string, string[]>();
  @state() private _loadingVersions = new Set<string>();
  @state() private _loading = true;
  @state() private _error = "";
  @state() protected _toastMessage = "";
  @state() private _history: PreviewHistoryEntry[] = [];
  @state() private _initialized = false;
  private _routeManuallySet = false;

  async connectedCallback() {
    super.connectedCallback();
    this._history = loadPreviewHistory();

    if (!this._initialized) {
      this._applyUrlParams();
      this._initialized = true;
    }

    if (!this.environments) {
      await this._loadEnvironments();
    } else {
      this._loading = false;
    }
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("environments") && this.environments) {
      this._loading = false;
    }

    if (changed.has("initialEnvironment") && this.initialEnvironment) {
      this._environment = this.initialEnvironment;
    }
    if (changed.has("initialOverrides") && this.initialOverrides) {
      this._overrides = new Map(this.initialOverrides);
    }
    if (changed.has("initialRoute") && this.initialRoute) {
      this._route = this.initialRoute;
      this._routeManuallySet = true;
    }

    if (changed.has("activeProductFilter") && !this._routeManuallySet) {
      this._route = getDefaultPreviewRoute(this.activeProductFilter);
    }

    this._syncOverrideSelectValues();

  }

  private _syncOverrideSelectValues() {
    if (!this.renderRoot) return;

    const overrideSelects = this.renderRoot.querySelectorAll("select[data-mfe-name]");

    for (const select of overrideSelects) {
      const name = select.getAttribute("data-mfe-name");
      if (!name) continue;

      const expectedValue = this._overrides.get(name) ?? "";
      if ((select as HTMLSelectElement).value !== expectedValue) {
        (select as HTMLSelectElement).value = expectedValue;
      }
    }
  }

  private _applyUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const decoded = decodeDashboardPreviewParams(params);

    if (decoded.environment) this._environment = decoded.environment;
    if (decoded.route) {
      this._route = decoded.route;
      this._routeManuallySet = true;
    }
    if (decoded.overrides) this._overrides = decoded.overrides;
  }

  private async _loadEnvironments() {
    this._loading = true;
    try {
      this.environments = await api.getEnvironments();
    } catch (e) {
      this._error = e instanceof Error ? e.message : "Failed to load environment data";
    } finally {
      this._loading = false;
    }
  }

  private _getMfeList(): Array<{ name: string; current: MfeVersion }> {
    if (!this.environments) return [];

    const envData = this.environments[this._environment];
    if (!envData) return [];

    const filtered = filterIgnoredMfes(envData.mfes);
    let names = Object.keys(filtered).sort();

    if (this.activeProductFilter && this.products) {
      names = filterMfeNames(names, this.activeProductFilter, this.products);
    }

    return names.map((name) => ({
      name,
      current: filtered[name],
    }));
  }

  private async _fetchVersions(mfeName: string) {
    if (this._availableVersions.has(mfeName) || this._loadingVersions.has(mfeName)) return;

    this._loadingVersions = new Set([...this._loadingVersions, mfeName]);
    try {
      const { versions } = await api.getMfeVersions(mfeName);
      this._availableVersions = new Map([...this._availableVersions, [mfeName, versions]]);
    } catch {
      this._availableVersions = new Map([...this._availableVersions, [mfeName, []]]);
    } finally {
      const next = new Set(this._loadingVersions);
      next.delete(mfeName);
      this._loadingVersions = next;
    }
  }

  private _handleEnvironmentChange(e: Event) {
    const value = (e.target as HTMLSelectElement).value as Environment;
    this._environment = value;
    this._overrides = new Map();
    this._selectedSnapshotId = "";
    this._availableVersions = new Map();
  }

  private _handleRouteChange(e: Event) {
    this._route = (e.target as HTMLInputElement).value;
    this._routeManuallySet = true;
  }

  private _handleOverrideChange(mfeName: string, version: string, currentVersion: string) {
    const next = new Map(this._overrides);
    if (version === currentVersion || version === "") {
      next.delete(mfeName);
    } else {
      next.set(mfeName, version);
    }
    this._overrides = next;
  }

  private _resetOverride(mfeName: string) {
    const next = new Map(this._overrides);
    next.delete(mfeName);
    this._overrides = next;
  }

  private _resetAll() {
    this._overrides = new Map();
    this._selectedSnapshotId = "";
  }

  protected _importFromSnapshot(snapshot: Snapshot) {
    if (!this.environments) return;

    this._environment = snapshot.environment;
    this._selectedSnapshotId = snapshot.id;
    this._availableVersions = new Map();
    const envData = this.environments[snapshot.environment];
    if (!envData) return;

    const overrides = buildOverridesFromVersions(snapshot.versions, envData.mfes);
    this._overrides = overrides;

    if (overrides.size === 0) {
      this._showToast(`Applied snapshot \"${snapshot.name}\": no differing versions in ${snapshot.environment}`);
      return;
    }

    const hiddenCount = this._countHiddenOverrides(overrides, snapshot.environment);
    const hiddenLabel = hiddenCount > 0 ? `, ${hiddenCount} hidden by the active product filter` : "";
    this._showToast(`Applied snapshot \"${snapshot.name}\" with ${overrides.size} override${overrides.size === 1 ? "" : "s"}${hiddenLabel}`);
  }

  protected _getPreviewConfig(): PreviewConfig {
    return {
      environment: this._environment,
      route: this._route,
      overrides: new Map(this._overrides),
    };
  }

  private _getPreviewUrl(): string {
    return buildPreviewUrl(this._getPreviewConfig());
  }

  private async _copyUrl() {
    const url = this._getPreviewUrl();
    try {
      await navigator.clipboard.writeText(url);
      this._showToast("URL copied to clipboard");
    } catch {
      // Fallback for non-HTTPS contexts
      this._showToast("Failed to copy URL");
    }
  }

  private _openPreview() {
    const url = this._getPreviewUrl();
    addPreviewHistoryEntry({
      url,
      environment: this._environment,
      overrideCount: this._overrides.size,
      timestamp: new Date().toISOString(),
    });
    this._history = loadPreviewHistory();
    window.open(url, "_blank", "noopener,noreferrer");
  }

  private _showToast(message: string) {
    this._toastMessage = message;
    setTimeout(() => {
      this._toastMessage = "";
    }, 2000);
  }

  private _filterSnapshotsByProduct(snapshots: Snapshot[]): Snapshot[] {
    if (!this.activeProductFilter || !this.products) return snapshots;
    const product = this.products.products[this.activeProductFilter];
    if (!product) return snapshots;
    const allowed = new Set(product.mfes);
    return snapshots.filter((s) => {
      // Keep snapshots that have at least one MFE in the active product
      if (s.product === this.activeProductFilter) return true;
      return Object.keys(s.versions).some((name) => allowed.has(name));
    });
  }

  private _getVisibleMfeNamesFor(environment: Environment = this._environment): Set<string> {
    if (!this.environments) return new Set();

    const envData = this.environments[environment];
    if (!envData) return new Set();

    const filtered = filterIgnoredMfes(envData.mfes);
    let names = Object.keys(filtered).sort();

    if (this.activeProductFilter && this.products) {
      names = filterMfeNames(names, this.activeProductFilter, this.products);
    }

    return new Set(names);
  }

  protected _countHiddenOverrides(
    overrides: Map<string, string> = this._overrides,
    environment: Environment = this._environment,
  ): number {
    const visibleNames = this._getVisibleMfeNamesFor(environment);
    let hiddenCount = 0;

    for (const name of overrides.keys()) {
      if (!visibleNames.has(name)) {
        hiddenCount += 1;
      }
    }

    return hiddenCount;
  }

  private _compareVersionParts(left: string, right: string): number {
    const leftIsNumeric = /^\d+$/.test(left);
    const rightIsNumeric = /^\d+$/.test(right);

    if (leftIsNumeric && rightIsNumeric) {
      return Number(left) - Number(right);
    }

    if (leftIsNumeric) return 1;
    if (rightIsNumeric) return -1;

    return left.localeCompare(right);
  }

  private _compareResolvedVersions(left: string, right: string): number {
    const leftParts = left.split(/[.-]/);
    const rightParts = right.split(/[.-]/);
    const maxLength = Math.max(leftParts.length, rightParts.length);

    for (let index = 0; index < maxLength; index += 1) {
      const leftPart = leftParts[index];
      const rightPart = rightParts[index];

      if (leftPart === undefined) return -1;
      if (rightPart === undefined) return 1;

      const comparison = this._compareVersionParts(leftPart, rightPart);
      if (comparison !== 0) {
        return comparison;
      }
    }

    return 0;
  }

  protected _getOverrideOptions(mfe: { name: string; current: MfeVersion }): string[] {
    const overriddenVersion = this._overrides.get(mfe.name);
    const loadedVersions = this._availableVersions.get(mfe.name) ?? [];
    const dedupedVersions = new Set(loadedVersions);

    if (overriddenVersion) {
      dedupedVersions.add(overriddenVersion);
    }

    return Array.from(dedupedVersions).sort((left, right) => this._compareResolvedVersions(right, left));
  }

  render() {
    if (this._loading) {
      return html`<div class="empty"><bee-spinner size="large" type="blue"></bee-spinner></div>`;
    }

    if (this._error) {
      return html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>`;
    }

    const mfeList = this._getMfeList();
    const previewConfig = this._getPreviewConfig();
    const previewUrl = buildPreviewUrl(previewConfig);
    const overrideCount = previewConfig.overrides.size;
    const hiddenOverrideCount = this._countHiddenOverrides(previewConfig.overrides);
    const allSnapshots = loadSnapshots();
    const snapshots = this._filterSnapshotsByProduct(allSnapshots);

    return html`
      ${this._renderControls()}
      ${this._renderQuickActions(snapshots)}
      ${this._renderMfeTable(mfeList)}
      ${this._renderUrlBar(previewUrl, overrideCount, hiddenOverrideCount)}
      ${this._renderHistory()}
      ${this._toastMessage ? html`<div class="toast">${this._toastMessage}</div>` : nothing}
    `;
  }

  private _renderControls() {
    return html`
      <div class="controls">
        <div class="control-group">
          <label>Base environment</label>
          <select @change=${this._handleEnvironmentChange}>
            <option value="dev" ?selected=${this._environment === "dev"}>dev</option>
            <option value="stage" ?selected=${this._environment === "stage"}>stage</option>
            <option value="prod" ?selected=${this._environment === "prod"}>prod</option>
          </select>
        </div>
        <div class="control-group">
          <label>Preview route</label>
          <input type="text" .value=${this._route} @input=${this._handleRouteChange} />
        </div>
      </div>
    `;
  }

  private _renderQuickActions(snapshots: Snapshot[]) {
    return html`
      <div class="quick-actions">
        <bee-button label="Reset all" bee-type="secondary-grey" size="small" @click=${this._resetAll} ?disabled=${this._overrides.size === 0}></bee-button>
        ${snapshots.length > 0 ? html`
          <div class="control-group">
            <select .value=${this._selectedSnapshotId} @change=${(e: Event) => {
              const id = (e.target as HTMLSelectElement).value;
              this._selectedSnapshotId = id;
              const snap = snapshots.find((s) => s.id === id);
              if (snap) this._importFromSnapshot(snap);
            }}>
              <option value="">Import from snapshot...</option>
              ${snapshots.map((s) => html`
                <option value=${s.id}>${s.name} (${s.environment})</option>
              `)}
            </select>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderMfeTable(mfeList: Array<{ name: string; current: MfeVersion }>) {
    if (mfeList.length === 0) {
      return html`<div class="empty">No MFEs found for this environment.</div>`;
    }

    return html`
      <table class="mfe-table">
        <thead>
          <tr>
            <th>MFE</th>
            <th>LATEST version</th>
            <th>Override version</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${mfeList.map((mfe) => this._renderMfeRow(mfe))}
        </tbody>
      </table>
    `;
  }

  private _renderMfeRow(mfe: { name: string; current: MfeVersion }) {
    const isOverridden = this._overrides.has(mfe.name);
    const overriddenVersion = this._overrides.get(mfe.name) ?? "";
    const isLoading = this._loadingVersions.has(mfe.name);
    const versions = this._getOverrideOptions(mfe);

    return html`
      <tr class=${isOverridden ? "overridden" : ""}>
        <td class="mfe-name">${mfe.name}</td>
        <td class="current-version">${mfe.current.resolved}</td>
        <td>
          <div class="override-cell">
            <select
              data-mfe-name=${mfe.name}
              .value=${live(overriddenVersion)}
              @focus=${() => this._fetchVersions(mfe.name)}
              @change=${(e: Event) => this._handleOverrideChange(mfe.name, (e.target as HTMLSelectElement).value, mfe.current.resolved)}
            >
              <option value="">-- LATEST --</option>
              ${isLoading ? html`<option disabled>Loading...</option>` : nothing}
              ${versions?.map((v) => html`
                <option value=${v}>${v}${v === mfe.current.resolved ? " (LATEST)" : ""}</option>
              `)}
            </select>
          </div>
        </td>
        <td>
          ${isOverridden ? html`
            <bee-button label="Reset" bee-type="secondary-grey" size="small" @click=${() => this._resetOverride(mfe.name)}></bee-button>
          ` : nothing}
        </td>
      </tr>
    `;
  }

  private _renderUrlBar(previewUrl: string, overrideCount: number, hiddenOverrideCount: number) {
    return html`
      <div class="url-bar">
        <div class="url-display" title=${previewUrl}>${previewUrl}</div>
        ${overrideCount > 0 ? html`<span class="override-badge">${overrideCount} override${overrideCount !== 1 ? "s" : ""}</span>` : nothing}
        ${hiddenOverrideCount > 0 ? html`<span class="override-badge">${hiddenOverrideCount} hidden</span>` : nothing}
        <bee-button label="Copy URL" bee-type="secondary-grey" size="small" @click=${this._copyUrl}></bee-button>
        <bee-button label="Open Preview" bee-type="primary" size="small" @click=${this._openPreview}></bee-button>
      </div>
    `;
  }

  private _renderHistory() {
    if (this._history.length === 0) return nothing;

    return html`
      <div class="history-section">
        <div class="history-title">Recent previews</div>
        <div class="history-list">
          ${this._history.map((entry) => html`
            <div class="history-item">
              <span class="history-url" title=${entry.url}>${entry.url}</span>
              <span class="history-meta">
                ${entry.environment} | ${entry.overrideCount} overrides | ${this._formatDate(entry.timestamp)}
              </span>
              <bee-button label="Open" bee-type="secondary-grey" size="small" @click=${() => window.open(entry.url, "_blank", "noopener,noreferrer")}></bee-button>
            </div>
          `)}
        </div>
      </div>
    `;
  }

  private _formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
